/* eslint-disable unicorn/prevent-abbreviations */
'use client';

import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import {
  type ComponentProps,
  createContext,
  type KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { cn } from '@bazaar-marketplace/utils';
import { DotButton, useDotButton } from './carousel-dot-button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

interface CarouselProps {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  images: string[];
}

type CarouselContextProps = CarouselProps & {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
};

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  images,
  ...props
}: CarouselProps & ComponentProps<'div'>) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      loop: true,
      duration: 100,
      axis: orientation === 'horizontal' ? 'x' : 'y',
      ...opts,
    },
    plugins,
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: opts?.axis === 'y' ? 'vertical' : 'horizontal',
        plugins: plugins || [],
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        images,
      }}
    >
      <div
        aria-roledescription="carousel"
        className={cn('relative', className)}
        data-slot="carousel"
        role="region"
        onKeyDownCapture={handleKeyDown}
        {...props}
      >
        {children}
        <div className="absolute flex gap-2">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              isActive={index === selectedIndex}
              onClick={() => {
                onDotButtonClick(index);
              }}
            />
          ))}
        </div>
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: ComponentProps<'div'>) {
  const { carouselRef } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden relative" data-slot="carousel-content">
      <div className={cn('flex -ml-4', className)} {...props} />
    </div>
  );
}

function CarouselItem({ className, ...props }: ComponentProps<'div'>) {
  const { orientation } = useCarousel();

  return (
    <div
      aria-roledescription="slide"
      className={cn('min-w-0 shrink-0 grow-0 basis-full', orientation === 'horizontal' ? 'pl-4' : 'pt-4', className)}
      data-slot="carousel-item"
      role="group"
      {...props}
    />
  );
}

export { type CarouselApi, Carousel, CarouselContent, CarouselItem };
